import { useState } from 'react'
import type { CommentEntityKind } from '@/entities/comment'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Textarea } from '@/shared/ui/field'
import { toast } from '@/shared/ui/toast'
import { useAddComment } from '../api/mutations'

interface CommentComposerProps {
  entityKind: CommentEntityKind
  entityId: string
  parentId?: string
  isEmployee: boolean
  // Родитель уже внутренний: ответ внутренний по определению — чекбокс не нужен.
  parentInternal?: boolean
  placeholder?: string
  submitLabel?: string
  onDone?: () => void
}

// Добавление комментария/ответа к сущности. internal-пометка — только сотруднику.
export function CommentComposer({
  entityKind,
  entityId,
  parentId,
  isEmployee,
  parentInternal = false,
  placeholder,
  submitLabel = 'Отправить',
  onDone,
}: CommentComposerProps) {
  const add = useAddComment(entityKind, entityId)
  const [text, setText] = useState('')
  const [internal, setInternal] = useState(false)

  // Ответ на внутренний комментарий всегда внутренний (заказчик его не видит),
  // поэтому пометку не спрашиваем, а проставляем.
  const effectiveInternal = parentInternal || internal

  function submit() {
    if (!text.trim()) return
    add.mutate(
      {
        text: text.trim(),
        ...(parentId ? { parent_id: parentId } : {}),
        ...(effectiveInternal ? { internal: true } : {}),
      },
      {
        onSuccess: () => {
          setText('')
          setInternal(false)
          onDone?.()
        },
        onError: () => toast.error('Не удалось отправить'),
      },
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        aria-label={parentId ? 'Ответ на комментарий' : 'Новый комментарий'}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder ?? 'Написать комментарий…'}
        className="min-h-20"
      />
      <div className="flex items-center justify-between gap-3">
        {isEmployee && !parentInternal ? (
          <Checkbox
            checked={internal}
            onChange={(e) => setInternal(e.target.checked)}
            label="Внутренний (не виден заказчику)"
            labelClassName="text-xs text-text-secondary"
          />
        ) : isEmployee && parentInternal ? (
          <span className="text-xs text-text-tertiary">Внутренний ответ — не виден заказчику</span>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          {onDone && (
            <Button variant="ghost" size="sm" onClick={onDone} disabled={add.isPending}>
              Отмена
            </Button>
          )}
          <Button size="sm" onClick={submit} disabled={add.isPending || !text.trim()}>
            {add.isPending ? 'Отправляем…' : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
